import { promoState, onPromoChange, readMap, pickLine, pickOption, formatMoney, paintReactive, thenText } from '@theme/aromely-pricing';

/**
 * Aromely fragrance PDP — gallery, size picker, diffuser fit tool,
 * purchase options and add-to-cart. Data comes from the
 * [data-afp-model] JSON island written by the section; every price comes
 * from the price line's map (snippets/aromely-price.liquid, both promotion states).
 */
class AromelyFragrancePdp {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    const island = root.querySelector('[data-afp-model]');
    if (!island) return;
    // A malformed data island (e.g. a stray quote typed into a section setting) must not
    // take the whole buy box down: log it and leave the server-rendered markup as is.
    try {
      this.m = JSON.parse(island.textContent);
    } catch (error) {
      console.error('Fragrance PDP: could not read the product data island.', error);
      this.m = null;
      return;
    }
    if (!this.m || !this.m.sizes || this.m.sizes.length === 0) return;
    this.t = this.m.strings;
    this.state = {
      size: this.m.selected || this.m.sizes[0].k,
      mode: 'once',
      plan: null,
      qty: 1,
      model: '',
    };
    if (!this.sizeOf(this.state.size)) this.state.size = this.m.sizes[0].k;
    this.plans = this.sizeOf(this.state.size).plans || [];
    this.ensurePlan(true);
    this.q = (sel) => this.root.querySelector(sel);
    this.qa = (sel) => Array.from(this.root.querySelectorAll(sel));
    // Read at paint time: a section re-render or morph can replace the map.
    this.pmap = () => readMap(this.root.querySelector('[data-afp-pricewrap] [data-price-map]'));
    const off = onPromoChange(() => {
      if (!this.root.isConnected) { off(); return; }
      this.paint();
    });
    this.root.addEventListener('aromely:price-stale', () => this.paint());
    this.busy = false;
    this.buildGallery();
    this.buildSizes();
    this.buildFit();
    this.bind();
    // The promotion module may mount after this controller's first price paint.
    document.addEventListener('aromely:promo-ready', () => {
      if (this.root.isConnected) this.paint();
    }, { once: true });
    this.paint();
    // A deep link (?variant=) selects a size; show that size's photo from the start.
    if (this.m.selected && String(this.m.selected) !== String(this.m.sizes[0].k)) this.galToSize(this.state.size, true);
  }

  /** Keep the shareable URL on the selected variant (Shopify's own ?variant= parameter). */
  syncUrl() {
    try {
      const size = this.sizeOf(this.state.size);
      if (!size || !window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(size.id));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) { /* URL sync is a convenience only */ }
  }

  /** Selecting a size from any control: keep plans, photo and URL in step. */
  selectSize(k) {
    const size = this.sizeOf(k);
    if (!size) return;
    this.state.size = size.k;
    this.plans = size.plans || [];
    this.ensurePlan(false);
    this.galToSize(this.state.size);
    this.syncUrl();
    this.paint();
  }

  /** Best-value plan for the current size: lowest per-delivery price, shortest cadence on a tie. */
  bestPlan() {
    if (this.plans.length === 0) return undefined;
    return this.plans.reduce((a, b) => (b.price < a.price || (b.price === a.price && b.every < a.every) ? b : a), this.plans[0]);
  }

  /** Keep state.plan valid for the current size; default to the best-value plan. */
  ensurePlan(reset) {
    const best = this.bestPlan();
    if (!best) {
      this.state.plan = null;
      this.state.mode = 'once';
      return;
    }
    if (reset || !this.plans.some((p) => p.every === this.state.plan)) this.state.plan = best.every;
  }

  /** @param {string} k @returns {object|undefined} */
  sizeOf(k) {
    return this.m.sizes.find((s) => s.k === String(k));
  }

  /**
   * One-time entry of a size for the browser's promotion state.
   * @param {object} size @returns {object|null}
   */
  once(size) {
    return pickOption(this.pmap(), size.id);
  }

  /**
   * First-order entry of a plan on a size (its one-time entry when the size has no such plan).
   * @param {object} size @param {object|undefined} plan @returns {object|null}
   */
  planEntry(size, plan) {
    return plan ? pickOption(this.pmap(), size.id, plan.id) : null;
  }

  /** @param {object} size @returns {string} */
  onceText(size) {
    const e = this.once(size);
    return e ? e.F : this.money(size.price);
  }

  /** Line total of an entry: its own money string for one, the per-unit figure × qty otherwise. */
  lineText(e, unit, qty) {
    return e ? (qty > 1 ? this.money(e.f * qty) : e.F) : this.money(unit * qty);
  }

  /** @param {number} cents @returns {string} */
  money(cents) {
    return formatMoney(cents, this.m.moneyFormat, this.m.currency);
  }

  /** @param {number} n @returns {string} */
  cadence(n) {
    return n === 1 ? this.t.everyOne : this.fill(this.t.every, { n });
  }

  /** @param {string} template @param {object} vars @returns {string} */
  fill(template, vars) {
    let out = template || '';
    for (const [key, value] of Object.entries(vars)) {
      out = out.replaceAll(`__${key.toUpperCase()}__`, () => String(value));
    }
    return out;
  }

  /* ---------------- gallery ---------------- */
  buildGallery() {
    this.track = this.q('[data-afp-track]');
    this.rail = this.q('[data-afp-rail]');
    this.dots = this.q('[data-afp-dots]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.g = 0;
    // Some fragrances name the smallest bottle's photo without a size in the
    // filename, so nothing matched it server-side: adopt the first image.
    const smallest = this.m.sizes[0].k;
    if (this.m.gallery.length > 0 && !this.m.gallery.some((g) => g.k === smallest)) {
      this.m.gallery[0].k = smallest;
      if (!this.m.gallery[0].cap) this.m.gallery[0].cap = this.m.sizes[0].label;
    }
    if (this.dots) {
      this.dots.innerHTML = this.slides.map((_, i) => `<i${i === 0 ? ' class="on"' : ''}></i>`).join('');
    }
    this.track.addEventListener('scroll', () => {
      if (this.galLock) return;
      const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      if (i !== this.g) {
        this.g = i;
        this.paintGallery();
      }
    }, { passive: true });
    for (const button of this.qa('[data-afp-g]')) {
      button.addEventListener('click', () => this.galGo(Number(button.dataset.afpG)));
    }
    const prev = this.q('[data-afp-galprev]');
    const next = this.q('[data-afp-galnext]');
    if (prev) prev.addEventListener('click', () => this.galGo(this.g - 1));
    if (next) next.addEventListener('click', () => this.galGo(this.g + 1));
    // The size label, counter and dots are JS-painted; paint them for the default
    // variant now instead of waiting for the first swipe or size change.
    this.paintGallery();
  }

  /** @param {number} i */
  galGo(i, instant) {
    const max = this.slides.length - 1;
    this.g = Math.max(0, Math.min(max, i));
    this.galLock = true;
    this.track.scrollTo({ left: this.g * this.track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    window.setTimeout(() => { this.galLock = false; }, 420);
    this.paintGallery();
  }

  paintGallery() {
    const meta = this.m.gallery[this.g] || {};
    const tag = this.q('[data-afp-galtag]');
    const count = this.q('[data-afp-galcount]');
    if (tag) {
      tag.textContent = meta.cap || '';
      tag.hidden = !meta.cap;
    }
    if (count) count.textContent = this.fill(this.t.imageOf, { n: this.g + 1, total: this.slides.length });
    for (const [i, button] of this.qa('[data-afp-g]').entries()) {
      const on = i === this.g;
      button.setAttribute('aria-current', String(on));
      if (on) button.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
    if (this.dots) {
      for (const [i, dot] of Array.from(this.dots.children).entries()) dot.className = i === this.g ? 'on' : '';
    }
    const prev = this.q('[data-afp-galprev]');
    const next = this.q('[data-afp-galnext]');
    if (prev) prev.disabled = this.g === 0;
    if (next) next.disabled = this.g === this.slides.length - 1;
  }

  /** @param {string} k */
  galToSize(k, instant) {
    const hit = this.m.gallery.find((g) => g.k === String(k));
    if (hit) this.galGo(hit.i, instant);
  }

  /* ---------------- sizes ---------------- */
  buildSizes() {
    const wrap = this.q('[data-afp-sizes]');
    if (!wrap) return;
    wrap.innerHTML = this.m.sizes.map((s) => {
      const low = s.avail && s.inv > 0 && s.inv < 10 ? '<i aria-hidden="true"></i>' : '';
      return `<button class="afp-size" type="button" data-afp-size="${s.k}" aria-pressed="false"${s.avail ? '' : ' disabled'}>
        ${low}<b>${s.tile}</b><u>${this.onceText(s)}</u>
      </button>`;
    }).join('');
    for (const button of this.qa('[data-afp-size]')) {
      button.addEventListener('click', () => this.selectSize(button.dataset.afpSize));
    }
  }

  /* ---------------- diffuser fit tool ---------------- */
  buildFit() {
    const grid = this.q('[data-afp-dgrid]');
    if (!grid) return;
    // Tiles are built as DOM nodes with textContent (merchant-typed names and
    // descriptions are never parsed as HTML).
    const tile = (key, name, desc, img) => {
      const button = document.createElement('button');
      button.className = 'afp-dtile';
      button.type = 'button';
      button.dataset.afpD = key;
      button.setAttribute('aria-pressed', 'false');
      if (img) {
        const image = document.createElement('img');
        image.src = img;
        image.alt = '';
        image.width = 54;
        image.height = 54;
        image.loading = 'lazy';
        button.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'afp-dtile__ph';
        placeholder.setAttribute('aria-hidden', 'true');
        button.append(placeholder);
      }
      const title = document.createElement('b');
      title.textContent = name || '';
      const sub = document.createElement('small');
      sub.textContent = desc || '';
      button.append(title, sub);
      return button;
    };
    grid.replaceChildren(
      ...this.m.fit.map((d) => tile(d.k, d.name, d.desc, d.img)),
      tile('__other', this.t.fitOther, '', ''),
      tile('__unsure', this.t.fitUnsure, '', '')
    );
    for (const button of this.qa('[data-afp-d]')) {
      button.addEventListener('click', () => {
        this.state.model = button.dataset.afpD;
        this.paintFit();
      });
    }
    const toggle = this.q('[data-afp-fit-toggle]');
    const panel = this.q('.afp-fit');
    if (toggle && panel) {
      toggle.addEventListener('click', () => {
        const open = panel.hasAttribute('hidden');
        panel.toggleAttribute('hidden', !open);
        toggle.setAttribute('aria-expanded', String(open));
      });
    }
  }

  paintFit() {
    for (const button of this.qa('[data-afp-d]')) {
      button.setAttribute('aria-pressed', String(button.dataset.afpD === this.state.model));
    }
    const out = this.q('[data-afp-fitout]');
    const ladder = this.q('[data-afp-ladder]');
    const label = this.q('[data-afp-fitk]');
    const warn = this.q('[data-afp-fitwarn]');
    if (!out || !ladder) return;
    const model = this.m.fit.find((d) => d.k === this.state.model);
    if (!model) {
      if (this.state.model === '__other' || this.state.model === '__unsure') {
        out.hidden = false;
        if (label) label.textContent = this.state.model === '__other' ? this.t.fitOther : this.t.fitUnsure;
        ladder.innerHTML = this.ladderRow(this.m.sizes[0], this.t.tryFirst, true);
        if (warn) warn.hidden = true;
        this.bindLadder();
        return;
      }
      out.hidden = true;
      return;
    }
    out.hidden = false;
    if (label) label.textContent = this.fill(this.t.fitFor, { model: model.name });
    const rows = [];
    const refill = this.sizeOf(model.refill);
    const more = this.sizeOf(model.more);
    if (refill) rows.push(this.ladderRow(refill, this.t.recommended, true));
    if (more && more.k !== (refill || {}).k) rows.push(this.ladderRow(more, this.t.wantMore, false));
    const sample = this.m.sample;
    if (sample && sample.avail) {
      rows.push(`<div class="afp-lrow"><span class="afp-lk">${this.t.tryFirst}</span>
        <span class="afp-lv">20 ml sample <span>${(pickLine(this.m.sample && this.m.sample.pm) || {}).F || this.money(sample.price)}</span></span>
        <button class="afp-btn afp-btn--ghost afp-btn--sm" type="button" data-afp-add-sample>${this.t.addSample || 'Add sample'}</button></div>`);
    }
    ladder.innerHTML = rows.join('');
    if (warn) warn.hidden = true;
    this.bindLadder();
  }

  /** @param {object} size @param {string} kicker @param {boolean} rec @returns {string} */
  ladderRow(size, kicker, rec) {
    const out = !size.avail;
    return `<div class="afp-lrow${rec ? ' afp-lrow--rec' : ''}">
      <span class="afp-lk">${kicker}</span>
      <span class="afp-lv">${size.label} <span>${this.onceText(size)}</span></span>
      <button class="afp-btn ${rec && !out ? 'afp-btn--primary' : 'afp-btn--ghost'} afp-btn--sm" type="button" data-afp-pick="${size.k}"${out ? ' disabled' : ''}>${out ? this.t.soldOut : (this.t.selectSize || this.t.select)}</button>
    </div>`;
  }

  bindLadder() {
    for (const button of this.qa('[data-afp-pick]')) {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.selectSize(button.dataset.afpPick);
        this.q('[data-afp-sizes]').scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    }
    for (const button of this.qa('[data-afp-add-sample]')) {
      if (button.dataset.afpBound) continue;
      button.dataset.afpBound = '1';
      button.addEventListener('click', () => this.addSample());
    }
  }

  /* ---------------- purchase options ---------------- */
  paintModes() {
    const block = this.q('[data-afp-modes-block]');
    if (!block) return;
    block.hidden = this.plans.length === 0;
    if (this.plans.length === 0) return;
    const size = this.sizeOf(this.state.size);
    const plan = this.currentPlan();
    for (const el of this.qa('[data-afp-mode]')) {
      el.setAttribute('aria-pressed', String(el.dataset.afpMode === this.state.mode));
    }
    for (const el of this.qa('[data-afp-mode-pick]')) {
      el.setAttribute('aria-pressed', String(el.dataset.afpModePick === this.state.mode));
    }
    const once = this.q('[data-afp-oncep]');
    const sub = this.q('[data-afp-subp]');
    const save = this.q('[data-afp-save]');
    const subs = this.q('[data-afp-subs]');
    const first = this.q('[data-afp-sub-first]');
    const on = this.state.mode === 'sub';
    const best = this.bestPlan();
    // Collapsed: advertise the best-value plan. Expanded: the plan the customer picked.
    const shown = on && plan ? plan : best;
    const sorted = this.plans.slice().sort((a, b) => a.every - b.every);
    // First-order entries; every percentage below is the entry's own derived saving.
    const e = shown ? this.planEntry(size, shown) : null;
    // The card, lead, chip and pills describe the recurring delivery: the plan price and
    // its saving off the one-time price. The sale's first-delivery price lives on the row
    // and the sub-first line only.
    const stacked = (ep) => promoState() === 'on' && !!ep && ep.r != null && ep.f < ep.r;
    const recur = (p) => {
      const ep = this.planEntry(size, p);
      const cents = ep && ep.r != null ? ep.r : ep ? ep.f : p.price;
      const pct = size.price > cents ? Math.round(((size.price - cents) * 100) / size.price) : 0;
      return { cents, text: ep && ep.r != null ? ep.R : ep ? ep.F : this.money(p.price), pct };
    };
    if (once) once.textContent = this.onceText(size);
    if (sub && shown) {
      const rs = recur(shown);
      sub.innerHTML = stacked(e)
        ? `<s>${e.R}</s> ${e.F} <small>${this.t.firstDelivery}</small>`
        : `${rs.pct > 0 ? `<s>${this.money(size.price)}</s> ` : ''}${rs.text} <small>${this.t.perDelivery}</small>`;
    }
    if (first && shown) {
      // Only while the sale is live in the browser and actually lowers the first delivery
      // below the renewals; the line is also Liquid-gated.
      // The lead sentence now carries the first-delivery price, so this line stays hidden.
      first.hidden = true;
      first.textContent = '';
    }
    if (save && best) {
      const pct = recur(best).pct;
      save.hidden = pct <= 0;
      save.textContent = `${this.t.bestValue} · ${this.fill(this.t.save, { pct: `${pct}%` })}`;
    }
    if (subs && best) {
      const others = sorted
        .filter((p) => p.every !== best.every)
        .map((p) => {
          return `${this.cadence(p.every).toLowerCase()} (${this.fill(this.t.save, { pct: `${recur(p).pct}%` }).toLowerCase()})`;
        });
      const ebest = this.planEntry(size, best);
      const lead = this.fill(stacked(ebest) ? this.t.subLeadFirst : this.t.subLead, {
        first: `<b>${ebest ? ebest.F : ''}</b>`,
        price: `<b>${recur(best).text}</b>`,
        cadence: this.cadence(best.every).toLowerCase(),
        pct: `<b>${recur(best).pct}%</b>`,
      });
      const more = others.length > 0 ? ` ${this.fill(this.t.subMore, { list: others.join(', ') })}` : '';
      subs.innerHTML = `${lead}${more} ${this.t.subTail}`;
    }
    const freq = this.q('[data-afp-freq]');
    const perks = this.q('[data-afp-perks]');
    const renew = this.q('[data-afp-renew]');
    if (freq) {
      freq.hidden = !on;
      freq.innerHTML = sorted
        .map((p) => {
          const rp = recur(p);
          const ep = this.planEntry(size, p);
          const st = stacked(ep);
          const isBest = best && p.every === best.every;
          const bits = [st ? this.fill(this.t.then, { price: rp.text }) : '', rp.pct > 0 ? `−${rp.pct}%` : '', isBest ? this.t.bestValue : ''].filter(Boolean);
          return `<button type="button" data-afp-plan="${p.every}" aria-pressed="${p.every === this.state.plan}">
            <small>${this.cadence(p.every)}</small><b>${st ? ep.F : rp.text}</b><span>${bits.join(' · ')}</span>
          </button>`;
        })
        .join('');
      for (const button of this.qa('[data-afp-plan]')) {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.state.plan = Number(button.dataset.afpPlan);
          this.paint();
          // paint() re-rendered these buttons: put keyboard focus back on the one just chosen.
          const again = this.q(`[data-afp-plan="${this.state.plan}"]`);
          if (again) again.focus();
        });
      }
    }
    if (perks) perks.hidden = !on;
    if (renew) {
      renew.hidden = !on;
      if (on && plan) renew.innerHTML = this.renewText((this.planEntry(size, plan) || {}).r ?? plan.price);
    }
  }

  /** Renewal wording for the current quantity: total per delivery, with the per-bottle price when more than one. */
  renewText(unitPrice) {
    const qty = this.state.qty;
    const total = this.money(unitPrice * qty);
    const price = qty > 1 ? `${total} (${qty} × ${this.money(unitPrice)})` : total;
    return this.fill(this.t.renew, { price });
  }

  /** @returns {object|undefined} */
  currentPlan() {
    if (this.plans.length === 0) return undefined;
    return this.plans.find((p) => p.every === this.state.plan) || this.plans[0];
  }

  /* ---------------- paint ---------------- */
  paint() {
    const size = this.sizeOf(this.state.size);
    const plan = this.state.mode === 'sub' ? this.currentPlan() : undefined;
    const unit = plan ? plan.price : size.price;
    const quantity = this.state.qty;
    // The entry painted on the row (plan or one-time) and the size's one-time entry.
    const e = plan ? this.planEntry(size, plan) : this.once(size);
    const h = this.once(size);
    // Share the exact selected first-delivery price, including its plan and quantity.
    // Every paint covers size, mode, cadence, quantity and promotion-state changes.
    if (e) {
      const offer = this.q('[data-promo-offer]');
      document.dispatchEvent(new CustomEvent('aromely:size', {
        detail: { resource: { id: size.id, price: unit, final_price: e.f, quantity }, data: { productId: offer?.dataset.productId } },
      }));
    }

    for (const button of this.qa('[data-afp-size]')) {
      button.setAttribute('aria-pressed', String(button.dataset.afpSize === this.state.size));
      const tile = this.sizeOf(button.dataset.afpSize);
      const tilePrice = button.querySelector('u');
      if (tile && tilePrice) tilePrice.textContent = this.onceText(tile);
    }

    // The header shows the one-time price for the browser's promotion state, struck when reduced.
    const hprice = this.q('[data-afp-hprice]');
    const hwas = this.q('[data-afp-hwas]');
    const hper = this.q('[data-afp-hper]');
    if (hprice) hprice.textContent = h ? h.F : this.money(size.price);
    if (hwas) {
      const strike = !!h && h.c > h.f;
      hwas.hidden = !strike;
      hwas.textContent = strike ? h.C : '';
    }
    if (hper) hper.textContent = size.label;

    const sizeline = this.q('[data-afp-sizeline]');
    if (sizeline) {
      const per = size.ml >= 1000 ? 100 : 10;
      const perPrice = this.money(((h ? h.f : size.price) / size.ml) * per);
      // Sellable with no counted stock (the gallon is made on demand, no delay) is not "In stock".
      const stock = !size.avail
        ? `<span class="low">${this.t.outOfStock}</span>`
        : size.inv <= 0
          ? (this.t.availToOrder || 'Made to order · no shipping delay')
          : size.inv < 10
            ? `<span class="low">${this.fill(this.t.lowStock, { n: size.inv })}</span>`
            : this.t.inStock;
      sizeline.innerHTML = `<b>${size.label}</b> · ${this.fill(this.t.perUnit, { price: perPrice, n: per })} · ${stock}`;
    }

    this.paintModes();
    this.paintFit();

    const price = this.q('[ref="afpPrice"]') || this.q('[data-afp-price]');
    const compare = this.q('[ref="afpCompare"]');
    const badge = this.q('[ref="afpBadge"]');
    const per = this.q('[data-afp-per]');
    if (e) {
      paintReactive({ price, compare, badge }, e, { quantity, format: this.m.moneyFormat, currency: this.m.currency });
    } else if (price) {
      price.textContent = this.money(unit * quantity);
    }
    // Mark the line painted in both branches, or the module's observer re-fires price-stale.
    const line = this.q('[data-afp-pricewrap] [data-price-map]');
    if (line) line.dataset.priceState = promoState();
    // While the sale lowers only the first delivery, the row price is that first delivery.
    const firstOnly = plan && promoState() === 'on' && e && e.r != null && e.f < e.r;
    if (per) per.textContent = plan ? (firstOnly ? this.t.firstDelivery : this.t.perDelivery) : '';
    // Renewal price while the sale runs and a plan is selected (× qty, like the row); same rule as the standard PDP.
    const then = this.q('[data-afp-then]');
    if (then) {
      const text = plan && promoState() === 'on' ? thenText(this.pmap(), e, quantity, this.m.moneyFormat, this.m.currency) : '';
      then.textContent = text;
      then.hidden = !text;
    }

    const qty = this.q('[data-afp-qty]');
    if (qty) qty.textContent = String(this.state.qty);

    const label = this.q('[data-afp-addlabel]');
    if (label) {
      label.textContent = size.avail
        ? `${this.t.addToCart} · ${this.lineText(e, unit, quantity)}`
        : this.t.soldOut;
    }
    const add = this.q('[data-afp-add]');
    if (add) add.disabled = !size.avail || this.busy;

    const note = this.q('[data-afp-ctanote]');
    const shipline = this.q('[data-afp-shipline]');
    const total = unit * this.state.qty;
    const free = this.m.ship.freeOver > 0 && total >= this.m.ship.freeOver;
    // Subscription orders ship free (the same promise the subscriber benefits list makes on this page).
    // No rate configured (0) → never print "$0.00 shipping"; the line just names the free threshold.
    const shipText = plan && this.t.shipSub
      ? this.t.shipSub
      : free
        ? this.t.shipFree
        : this.m.ship.flat > 0 && this.m.ship.freeOver > 0
          ? this.fill(this.t.shipFlat, { price: this.money(this.m.ship.flat), over: this.money(this.m.ship.freeOver) })
          : this.m.ship.freeOver > 0
            ? this.fill(this.t.shipOver || this.t.shipFlat, { price: '', over: this.money(this.m.ship.freeOver) })
            : '';
    if (note) {
      note.innerHTML = plan ? this.renewText(e && e.r != null ? e.r : unit) : '';
      note.hidden = !plan;
    }
    if (shipline) {
      shipline.innerHTML = shipText;
      shipline.hidden = !shipText;
    }

    const variantInput = this.q('[data-afp-variant-input]');
    const qtyInput = this.q('[data-afp-qty-input]');
    const planInput = this.q('[data-afp-plan-input]');
    if (variantInput) variantInput.value = String(size.id);
    if (qtyInput) qtyInput.value = String(this.state.qty);
    if (planInput) planInput.value = plan ? String(plan.id) : '';

    for (const table of document.querySelectorAll('[data-afp-plantable]')) {
      table.hidden = table.dataset.afpPlantable !== String(this.state.size);
    }

    const stickTitle = this.q('[data-afp-sticktitle]');
    const stickSub = this.q('[data-afp-sticksub]');
    if (stickTitle) stickTitle.textContent = `${this.m.name} · ${size.label}${this.state.qty > 1 ? ` × ${this.state.qty}` : ''}`;
    if (stickSub) {
      stickSub.textContent = `${plan ? this.cadence(plan.every) : this.t.oneTime} · ${this.lineText(e, unit, quantity)}`;
    }
    const stickBtn = this.q('[data-afp-stickbtn]');
    if (stickBtn) {
      stickBtn.disabled = !size.avail || this.busy;
      stickBtn.textContent = size.avail ? (this.t.add || 'Add') : this.t.soldOut;
    }
  }

  /* ---------------- events ---------------- */
  bind() {
    for (const card of this.qa('[data-afp-mode]')) {
      card.addEventListener('click', () => {
        if (this.state.mode === card.dataset.afpMode) return;
        this.state.mode = card.dataset.afpMode;
        this.paint();
      });
    }
    for (const button of this.qa('[data-afp-mode-pick]')) {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.state.mode === button.dataset.afpModePick) return;
        this.state.mode = button.dataset.afpModePick;
        this.paint();
        const again = this.q(`[data-afp-mode-pick="${this.state.mode}"]`);
        if (again) again.focus();
      });
    }
    for (const button of this.qa('[data-afp-q]')) {
      button.addEventListener('click', () => {
        this.state.qty = Math.max(1, Math.min(99, this.state.qty + Number(button.dataset.afpQ)));
        this.paint();
      });
    }
    const form = this.q('[data-afp-form]');
    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addCurrent();
      });
    }
    const stickBtn = this.q('[data-afp-stickbtn]');
    if (stickBtn) stickBtn.addEventListener('click', () => this.addCurrent());
    for (const button of document.querySelectorAll('[data-afp-add-sample]')) {
      if (button.dataset.afpBound) continue;
      button.dataset.afpBound = '1';
      button.addEventListener('click', () => this.addSample());
    }
    for (const button of document.querySelectorAll('[data-afp-open-finder]')) {
      if (button.dataset.afpFinderBound) continue;
      button.dataset.afpFinderBound = '1';
      button.addEventListener('click', () => AromelyFragrancePdp.openFinder());
    }
    this.watchSticky();
  }

  static openFinder() {
    const finder = document.getElementById('aromely-fragrance-finder');
    if (!finder) return;
    finder.open = true;
    finder.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  watchSticky() {
    const bar = this.q('[data-afp-stick]');
    const anchor = this.q('[data-afp-add]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }, { rootMargin: '0px 0px -60px 0px' });
    observer.observe(anchor);
  }

  /* ---------------- cart ---------------- */
  /** @returns {string[]} */
  drawerSectionIds() {
    return Array.from(document.querySelectorAll('aromely-cart-drawer-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  async addCurrent() {
    const size = this.sizeOf(this.state.size);
    if (!size || !size.avail) return;
    const plan = this.state.mode === 'sub' ? this.currentPlan() : undefined;
    const item = { id: size.id, quantity: this.state.qty };
    if (plan) item.selling_plan = plan.id;
    await this.add(item, this.q('[data-afp-add]'));
  }

  async addSample() {
    if (!this.m.sample || !this.m.sample.avail) return;
    await this.add({ id: this.m.sample.id, quantity: 1 }, this.q('[data-afp-add-sample]'));
  }

  /**
   * @param {object} item
   * @param {HTMLElement|null} button
   */
  /** Every control that can start a cart request. */
  purchaseControls() {
    return this.qa('[data-afp-add], [data-afp-stickbtn], [data-afp-add-sample], [data-afp-pick]');
  }

  async add(item, button) {
    if (this.busy) return;
    this.busy = true;
    const sections = this.drawerSectionIds();
    const controls = this.purchaseControls();
    const wasDisabled = new Map(controls.map((el) => [el, el.disabled]));
    for (const el of controls) { el.disabled = true; el.setAttribute('aria-busy', 'true'); }
    if (button) button.dataset.state = 'loading';
    try {
      const response = await fetch(this.m.cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [item],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      let itemCount = cart.item_count;
      for (const html of Object.values(cart.sections || {})) {
        const text = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[ref="cartItemCount"]')?.textContent;
        if (text) {
          itemCount = parseInt(text, 10);
          break;
        }
      }
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'aromely-fragrance-pdp',
          data: { source: 'aromely-fragrance-pdp', itemCount, sections: cart.sections },
        },
      }));
    } catch (error) {
      window.alert(this.t.cartError);
    } finally {
      this.busy = false;
      for (const el of controls) { el.disabled = wasDisabled.get(el) || false; el.removeAttribute('aria-busy'); }
      if (button) delete button.dataset.state;
      this.paint();
    }
  }
}

/** Boot every fragrance PDP root on the page (and after a section re-render). */
function afpBoot() {
  for (const root of document.querySelectorAll('[data-afp-root]')) {
    if (root.dataset.afpReady) continue;
    root.dataset.afpReady = '1';
    new AromelyFragrancePdp(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', afpBoot);
} else {
  afpBoot();
}
document.addEventListener('shopify:section:load', afpBoot);

